import { useState, useEffect } from 'react';

export default function IntercompanyPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    from_entity_id: '',
    to_entity_id: '',
    transaction_date: '',
    amount: '',
    currency: 'USD',
    description: '',
  });

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/entities/intercompany-transactions');
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load IC transactions');
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.from_entity_id || !form.to_entity_id || !form.amount || !form.transaction_date) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/entities/intercompany-transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_entity_id: form.from_entity_id,
          to_entity_id: form.to_entity_id,
          transaction_date: form.transaction_date,
          amount_cents: Math.round(parseFloat(form.amount) * 100),
          currency: form.currency.toUpperCase(),
          description: form.description,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to create IC transaction');
      setForm({ from_entity_id: '', to_entity_id: '', transaction_date: '', amount: '', currency: 'USD', description: '' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  async function eliminate(id) {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`/entities/intercompany-transactions/${id}/eliminate`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Elimination failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function fmtAmount(cents, currency) {
    const val = (cents / 100).toFixed(2);
    return `${currency} ${val}`;
  }

  return (
    <section data-testid="entities-intercompany-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Intercompany Transactions</h1>
          <p className="muted">Record and eliminate intercompany balances before consolidation.</p>
        </div>
        <button className="btn primary" data-testid="entity-ic-add" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add Transaction'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="entities-intercompany-error">{error}</div>}

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>From entity ID
            <input value={form.from_entity_id} onChange={(e) => setForm((f) => ({ ...f, from_entity_id: e.target.value }))} placeholder="entity id" />
          </label>
          <label>To entity ID
            <input value={form.to_entity_id} onChange={(e) => setForm((f) => ({ ...f, to_entity_id: e.target.value }))} placeholder="entity id" />
          </label>
          <label>Date
            <input type="date" value={form.transaction_date} onChange={(e) => setForm((f) => ({ ...f, transaction_date: e.target.value }))} />
          </label>
          <label>Amount
            <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="1000.00" />
          </label>
          <label>Currency
            <input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} placeholder="USD" maxLength={3} />
          </label>
          <label>Description
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="e.g. Management fee" />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.from_entity_id || !form.to_entity_id || !form.amount || !form.transaction_date}>Save</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="entity-ic-list">
          <thead>
            <tr><th>From</th><th>To</th><th>Date</th><th>Amount</th><th>Description</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((tx) => (
              <tr key={tx.id} data-testid={`entity-ic-row-${tx.id}`}>
                <td>{tx.from_entity_id}</td>
                <td>{tx.to_entity_id}</td>
                <td>{tx.transaction_date}</td>
                <td>{fmtAmount(tx.amount_cents || 0, tx.currency)}</td>
                <td>{tx.description || '—'}</td>
                <td><span className={`badge ${tx.eliminated ? 'success' : ''}`}>{tx.eliminated ? 'eliminated' : 'open'}</span></td>
                <td>
                  {!tx.eliminated && (
                    <button className="btn ghost" data-testid={`ic-eliminate-${tx.id}`} disabled={busy} onClick={() => eliminate(tx.id)}>
                      Eliminate
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="muted">No intercompany transactions.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
